import {
  useState,
} from 'react';
import {
  Callout,
  Field,
  Modal,
  TextInput,
  Chip,
  Loading,
  explainFailure,
  isApiClientError,
  useLoad,
} from '@affdice/portal-kit';
import {
  PlatformApi,
  type IdentityProfile,
} from '../../client.ts';
import {
  enrollmentKey,
  suspensionPlan,
  deactivationPlan,
} from '../../screens.ts';
import { t, tCount } from '../../messages.ts';
import { Failure } from '../shared.tsx';

type SuspendScope = 'everywhere' | 'operators' | 'brands';

// A function, not a constant: the words are read when the popup renders, once
// the browser's language is known.
const suspendScopes = (): readonly { id: SuspendScope; title: string; note: string }[] => [
  {
    id: 'brands',
    title: t('aff.suspend.scopeBrands'),
    note: t('aff.suspend.scopeBrandsNote'),
  },
  {
    id: 'operators',
    title: t('aff.suspend.scopeOperators'),
    note: t('aff.suspend.scopeOperatorsNote'),
  },
  {
    id: 'everywhere',
    title: t('aff.suspend.scopeEverywhere'),
    note: t('aff.suspend.scopeEverywhereNote'),
  },
];

/**
 * Suspend, with its reach chosen first.
 *
 * Three answers to "where". Chosen brands: the per-brand suspension the
 * enrolment table already offers, once per brand. Chosen operators: the
 * operator-wide deactivation the status chip makes, at each operator ticked —
 * every brand there at once, so the person reads as deactivated at that
 * operator rather than active with every brand suspended. Every operator: the
 * platform-wide identity status, exactly as the old single button set it.
 * Each is reversed where it is shown, and nothing here invents a state the
 * rest of the platform does not know.
 *
 * The line above the button says what the press will change before it is
 * pressed; a brand already suspended, or a profile not active, is left alone
 * rather than written again. Writes go one per brand or profile, each inside
 * its own operator's context; any that are refused are named, and the rest
 * stand. A deactivation refused for a payable balance can be forced with a
 * reason on record, as on the chip.
 */
export function SuspendAffiliateModal({
  api,
  identityId,
  name,
  onClose,
  onDone,
}: {
  api: PlatformApi;
  identityId: string;
  name: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [scope, setScope] = useState<SuspendScope>('brands');
  const [chosenOperators, setChosenOperators] = useState<ReadonlySet<string>>(new Set());
  const [chosenBrands, setChosenBrands] = useState<ReadonlySet<string>>(new Set());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  // Once any write has landed, closing must re-read the popup behind this.
  const [changed, setChanged] = useState(false);
  const [failures, setFailures] = useState<readonly { label: string; message: string }[]>([]);
  // Operators whose deactivation was refused for a payable balance, and the
  // chip's own way past it: force, with a reason on record.
  const [balanceRefused, setBalanceRefused] = useState<ReadonlySet<string>>(new Set());
  const [force, setForce] = useState(false);
  const [justification, setJustification] = useState('');
  const loaded = useLoad(() => api.identityProfiles(identityId), [identityId, version]);
  const profiles: readonly IdentityProfile[] = loaded.state === 'ready' ? loaded.value.profiles : [];
  const plan = scope === 'brands' ? suspensionPlan(profiles, chosenBrands) : null;
  const deactivation = scope === 'operators' ? deactivationPlan(profiles, chosenOperators) : null;
  const forcing = scope === 'operators' && force && balanceRefused.size > 0;

  const flip = (set: ReadonlySet<string>, key: string, on: boolean): ReadonlySet<string> => {
    const next = new Set(set);
    if (on) next.add(key); else next.delete(key);
    return next;
  };

  const summary = scope === 'everywhere'
    ? loaded.state === 'ready'
      ? t('aff.suspend.summaryEverywhere', { name, profiles: tCount('aff.count.profile', profiles.length) })
      : t('aff.suspend.summaryEverywhereLoading', { name })
    : deactivation
      ? deactivation.targets.length === 0
        ? t('aff.suspend.summaryNothing')
        : t('aff.suspend.summaryOperators', { name, operators: tCount('aff.count.operator', deactivation.targets.length) })
      : plan!.targets.length === 0
        ? t('aff.suspend.summaryNothing')
        : plan!.alreadySuspended > 0
          ? t('aff.suspend.summaryBrandsSkipped', {
              brands: tCount('aff.count.brand', plan!.targets.length),
              operators: tCount('aff.count.operator', plan!.operators),
              skipped: tCount('aff.count.brand', plan!.alreadySuspended),
            })
          : t('aff.suspend.summaryBrands', {
              brands: tCount('aff.count.brand', plan!.targets.length),
              operators: tCount('aff.count.operator', plan!.operators),
            });

  // The platform-wide suspension is one call about the person, so it does not
  // wait on the list of profiles the other two scopes choose from.
  const chosenCount = deactivation ? deactivation.targets.length : plan ? plan.targets.length : 0;
  const canSubmit = !busy && reason.trim() !== '' &&
    (scope === 'everywhere' || (loaded.state === 'ready' && chosenCount > 0)) &&
    (!forcing || justification.trim() !== '');

  const pickScope = (next: SuspendScope) => {
    setScope(next);
    setFailures([]);
    setBalanceRefused(new Set());
    setForce(false);
  };

  const close = changed ? onDone : onClose;

  const submit = async () => {
    setBusy(true);
    setFailures([]);
    try {
      if (scope === 'everywhere') {
        await api.changeIdentityStatus(identityId, 'SUSPENDED', reason.trim());
        onDone();
        return;
      }
      if (deactivation) {
        const chosen = deactivation.targets;
        const outcomes = await Promise.allSettled(
          chosen.map((t) =>
            api.changeAffiliateStatus(t.operatorId, t.affiliateId, {
              to: 'DEACTIVATED',
              reasonCode: reason.trim(),
              ...(forcing && balanceRefused.has(t.operatorId)
                ? { force: true, justification: justification.trim() }
                : {}),
            }),
          ),
        );
        const refused = outcomes.flatMap((r, i) =>
          r.status === 'rejected' ? [{ target: chosen[i]!, error: r.reason as unknown }] : [],
        );
        if (refused.length === 0) {
          onDone();
          return;
        }
        if (refused.length < chosen.length) setChanged(true);
        setFailures(refused.map((x) => ({ label: x.target.operator, message: explainFailure(x.error).message })));
        setBalanceRefused(new Set(
          refused
            .filter((x) => isApiClientError(x.error) && x.error.code === 'PENDING_BALANCE')
            .map((x) => x.target.operatorId),
        ));
        // The refused stay ticked, so a retry — forced where a balance was
        // the reason — sends exactly those again; the rest have landed.
        setChosenOperators(new Set(refused.map((x) => x.target.operatorId)));
        setVersion((v) => v + 1);
        return;
      }
      const targets = plan!.targets;
      const results = await Promise.allSettled(
        targets.map((t) =>
          api.setBrandEnrollmentStatus(t.operatorId, t.affiliateId, t.brandId, 'SUSPENDED', reason.trim()),
        ),
      );
      const failed = results.flatMap((r, i) =>
        r.status === 'rejected'
          ? [{ label: `${targets[i]!.brand} (${targets[i]!.operator})`, message: explainFailure(r.reason).message }]
          : [],
      );
      if (failed.length === 0) {
        onDone();
        return;
      }
      if (failed.length < targets.length) setChanged(true);
      setFailures(failed);
      setChosenOperators(new Set());
      setChosenBrands(new Set());
      setVersion((v) => v + 1);
    } catch (e) {
      setFailures([{ label: t('aff.suspend.failureLabel'), message: explainFailure(e).message }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    // Not closable while writes are in flight: the popup behind this re-reads
    // only when this one reports back, and a close mid-write would skip that.
    <Modal wide title={t('aff.suspend.title', { name })} onClose={busy ? () => {} : close}>
      <div className="form">
        {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}

        <Field group label={t('aff.suspend.where')}>
          <div className="scope-options" role="radiogroup" aria-label={t('aff.suspend.whereAria')}>
            {suspendScopes().map((s) => (
              <button
                key={s.id}
                type="button"
                role="radio"
                aria-checked={scope === s.id}
                className={`scope-option${scope === s.id ? ' is-selected' : ''}`}
                onClick={() => pickScope(s.id)}
              >
                <span className="scope-option-title">{s.title}</span>
                <span className="scope-option-note">{s.note}</span>
              </button>
            ))}
          </div>
        </Field>

        {loaded.state === 'loading' ? <Loading rows={2} /> : null}

        {scope === 'everywhere' ? (
          <Callout tone="caution">
            {t('aff.suspend.everywhereNote')}
          </Callout>
        ) : null}

        {loaded.state === 'ready' && scope === 'operators' ? (
          <Field group label={t('aff.suspend.operators')}>
            <div className="pick-group">
              {profiles.map((p) => {
                // Only an ACTIVE profile can be deactivated; any other state
                // is shown for what it is and left alone.
                const disabled = p.status !== 'ACTIVE';
                return (
                  <div key={p.affiliate_id} className={`pick-row${disabled ? ' is-disabled' : ''}`}>
                    <label>
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={!disabled && chosenOperators.has(p.operator_id)}
                        onChange={(e) => setChosenOperators((prev) => flip(prev, p.operator_id, e.target.checked))}
                      />
                      {p.operator}
                    </label>
                    <span className="faint">
                      {disabled
                        ? t('aff.suspend.already', { status: p.status.toLowerCase().replace(/_/g, ' ') })
                        : p.brands.length === 0
                          ? t('aff.suspend.notEnrolled')
                          : t('aff.suspend.everyBrandHere', { brands: tCount('aff.count.brand', p.brands.length) })}
                    </span>
                  </div>
                );
              })}
            </div>
          </Field>
        ) : null}

        {loaded.state === 'ready' && scope === 'brands' ? (
          <Field group label={t('aff.suspend.brands')}>
            <div className="pick-groups">
              {profiles.map((p) => (
                <div key={p.affiliate_id} className="pick-group">
                  <div className="pick-group-head">
                    <span>{p.operator}</span>
                    <span className="faint">{tCount('aff.count.brand', p.brands.length)}</span>
                  </div>
                  {p.brands.length === 0 ? (
                    <div className="pick-row is-disabled"><label>{t('aff.suspend.notEnrolledHere')}</label></div>
                  ) : (
                    p.brands.map((b) => {
                      const key = enrollmentKey(p.affiliate_id, b.brand_id);
                      const suspended = b.state !== 'ACTIVE';
                      return (
                        <div key={key} className={`pick-row${suspended ? ' is-disabled' : ''}`}>
                          <label>
                            <input
                              type="checkbox"
                              disabled={suspended}
                              checked={!suspended && chosenBrands.has(key)}
                              onChange={(e) => setChosenBrands((prev) => flip(prev, key, e.target.checked))}
                            />
                            {b.brand}
                          </label>
                          <Chip tone={suspended ? 'overdue' : 'locked'}>{suspended ? t('aff.suspend.chipSuspended') : t('aff.suspend.chipActive')}</Chip>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>
          </Field>
        ) : null}

        <Field label={t('common.reason')} hint={t('aff.suspend.reasonHint')}>
          <TextInput value={reason} onChange={setReason} />
        </Field>

        {failures.length > 0 ? (
          <Callout tone="error">
            {failures.length === 1 ? t('aff.suspend.didNotChangeOne') : t('aff.suspend.didNotChangeMany')}
            <ul>
              {failures.map((f, i) => <li key={i}>{f.label} — {f.message}</li>)}
            </ul>
          </Callout>
        ) : null}

        {scope === 'operators' && balanceRefused.size > 0 ? (
          <>
            <Field group label={t('aff.status.forceGroup')}>
              <label className="checkbox-stack-row">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                {' '}{t('aff.suspend.forceCheck', { operators: tCount('aff.count.operator', balanceRefused.size) })}
              </label>
            </Field>
            {force ? (
              <Field label={t('aff.status.why')}>
                <TextInput value={justification} onChange={setJustification} />
              </Field>
            ) : null}
          </>
        ) : null}

        <Callout>{summary}</Callout>

        <div className="form-actions">
          <button type="button" className="copy" disabled={busy} onClick={close}>{t('common.cancel')}</button>
          <button type="button" className="danger" disabled={!canSubmit} onClick={() => void submit()}>
            {busy
              ? deactivation ? t('aff.suspend.deactivating') : t('aff.suspend.suspending')
              : scope === 'everywhere'
                ? t('aff.suspend.submitEverywhere')
                : deactivation
                  ? chosenCount > 0
                    ? t('aff.suspend.submitDeactivateAt', { operators: tCount('aff.count.operator', chosenCount) })
                    : t('aff.suspend.submitDeactivate')
                  : chosenCount > 0
                    ? t('aff.suspend.submitSuspendOn', { brands: tCount('aff.count.brand', chosenCount) })
                    : t('aff.suspend.submit')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
